import { Tooltip } from '@ibsys/kit';
import { AlertTriangleIcon, SigmaIcon } from 'lucide-react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { PointsRuleAttention } from '@/entities/expert-report';
import type { PointsRuleOwnerKind } from '@/features/expert-report/pointsRuleOwnerKind';

type Props = {
  /** Результат считается особым правилом расчёта — не тем, что задано по умолчанию */
  hasCustomPointsRule?: boolean;
  /** Правило расчёта требует внимания; `null` — не требует */
  attention?: PointsRuleAttention | null;
  /** Правило расчёта требует внимания у скрытого внутри узла элемента; `null` — не требует */
  nestedAttention?: PointsRuleAttention | null;
  /** Чей это результат — от этого зависят слова подсказок; по умолчанию блок прежнего режима */
  ownerKind?: PointsRuleOwnerKind;
};

/** Подсказка отметки особого правила */
const CUSTOM_HINT_KEY = {
  block: 'customPointsRuleHint',
  group: 'km.customPointsRuleHint',
} as const;

/** Подсказка отметки «требует внимания» — по тому, чем именно правило разошлось с деревом */
const ATTENTION_HINT_KEY = {
  block: {
    inapplicable: 'inapplicablePointsRuleHint',
    missingChildren: 'missingChildrenPointsRuleHint',
  },
  group: {
    inapplicable: 'km.inapplicablePointsRuleHint',
    missingChildren: 'km.missingChildrenPointsRuleHint',
  },
} as const;

/** Подсказка отметки о скрытом внутри элементе — называет ту же причину, но у вложенного правила */
const NESTED_ATTENTION_HINT_KEY = {
  block: {
    inapplicable: 'nestedInapplicablePointsRuleHint',
    missingChildren: 'nestedMissingChildrenPointsRuleHint',
  },
  group: {
    inapplicable: 'km.nestedInapplicablePointsRuleHint',
    missingChildren: 'km.nestedMissingChildrenPointsRuleHint',
  },
} as const;

/**
 * Отметки правила расчёта в дереве: особое условие оценивания и правило, требующее внимания —
 * как своё, так и правило элемента, скрытого внутри свёрнутого узла.
 *
 * Правило владельца в карточке самого вложенного элемента не видно, поэтому его расхождение с
 * деревом показывается прямо в дереве — до того, как оно проявится результатом по диалогу.
 * Об этом же расхождении говорит предупреждение после сохранения. Отметка о скрытом элементе —
 * приглушённая: она говорит не об этом узле, а о том, что расхождение ждёт внутри него.
 *
 * Отметки в обоих режимах заключения одни и те же, разнятся лишь слова подсказок: прежний режим
 * считает по блокам, модель знаний — по группам и единицам оценивания.
 */
export const BlockPointsRuleMarks: FC<Props> = ({
  hasCustomPointsRule,
  attention,
  nestedAttention,
  ownerKind = 'block',
}) => {
  const { t } = useTranslation('expertReport');

  if (!hasCustomPointsRule && !attention && !nestedAttention) return null;

  return (
    <>
      {hasCustomPointsRule && (
        <Tooltip tooltip={t(CUSTOM_HINT_KEY[ownerKind])}>
          <SigmaIcon className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
        </Tooltip>
      )}
      {attention && (
        <Tooltip tooltip={t(ATTENTION_HINT_KEY[ownerKind][attention])}>
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning-foreground" />
        </Tooltip>
      )}
      {nestedAttention && (
        <Tooltip tooltip={t(NESTED_ATTENTION_HINT_KEY[ownerKind][nestedAttention])}>
          <AlertTriangleIcon className="mt-0.5 size-3.5 shrink-0 text-warning-foreground/60" />
        </Tooltip>
      )}
    </>
  );
};
